import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Search, Trash2, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STAGE_STYLES = {
  "New Lead": "bg-blue-50 text-blue-700 border-blue-200",
  "Documents Collected": "bg-amber-50 text-amber-700 border-amber-200",
  "University Applied": "bg-purple-50 text-purple-700 border-purple-200",
  "Accepted": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Visa Processing": "bg-orange-50 text-orange-700 border-orange-200",
  "Enrolled": "bg-teal-50 text-teal-700 border-teal-200",
};

export default function Students() {
  const { t, ts, tc, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState({ stages: [], countries: [] });
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (stageFilter && stageFilter !== "all") params.stage = stageFilter;
    api.get("/students", { params })
      .then((r) => { setStudents(r.data); setSelected([]); })
      .catch(() => {});
  };

  const toggleOne = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const allSelected = students.length > 0 && selected.length === students.length;
  const toggleAll = () => {
    setSelected(allSelected ? [] : students.map((s) => s.id));
  };

  const exportCsv = () => {
    if (students.length === 0) {
      toast.error(lang === "ar" ? "لا يوجد طلاب للتصدير" : "Nothing to export");
      return;
    }
    const headers = lang === "ar"
      ? ["الاسم", "الهاتف", "البريد", "الجنسية", "المستوى التعليمي", "رقم الجواز", "الدول المستهدفة", "المرحلة", "تاريخ الإضافة"]
      : ["Name", "Phone", "Email", "Nationality", "Education level", "Passport", "Target countries", "Stage", "Created"];

    // Wrap every field in quotes and double any quote inside, per RFC 4180
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = students.map((s) => [
      s.name, s.phone, s.email, s.nationality, s.education_level, s.passport_number,
      (s.target_countries || []).join(" | "),
      s.stage,
      s.created_at ? String(s.created_at).slice(0, 10) : "",
    ].map(esc).join(","));

    // The BOM tells Excel the file is UTF-8, otherwise Arabic arrives as mojibake
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mer-students-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(lang === "ar" ? `تم تصدير ${students.length} طالب` : `Exported ${students.length} student(s)`);
  };

  const deleteSelected = async () => {
    const names = students.filter((s) => selected.includes(s.id)).map((s) => s.name);
    const preview = names.slice(0, 5).join("\n• ");
    const more = names.length > 5
      ? (lang === "ar" ? `\n… و${names.length - 5} غيرهم` : `\n… and ${names.length - 5} more`)
      : "";
    const msg = lang === "ar"
      ? `سيتم حذف ${names.length} طالب نهائياً مع ملاحظاتهم ومهامهم ومستنداتهم:\n\n• ${preview}${more}\n\nلا يمكن التراجع. متابعة؟`
      : `This will permanently delete ${names.length} student(s) along with their notes, tasks and documents:\n\n• ${preview}${more}\n\nThis cannot be undone. Continue?`;
    if (!window.confirm(msg)) return;

    setDeleting(true);
    // Delete one by one so a single failure doesn't hide the rest
    const failed = [];
    for (const id of selected) {
      try {
        await api.delete(`/students/${id}`);
      } catch (_e) {
        failed.push(students.find((s) => s.id === id)?.name || id);
      }
    }
    setDeleting(false);

    const ok = selected.length - failed.length;
    if (ok > 0) {
      toast.success(lang === "ar" ? `تم حذف ${ok} طالب` : `Deleted ${ok} student(s)`);
    }
    if (failed.length > 0) {
      toast.error(lang === "ar"
        ? `تعذّر حذف: ${failed.join("، ")}`
        : `Could not delete: ${failed.join(", ")}`);
    }
    load();
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, stageFilter]);

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="eyebrow">{t("appName")}</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0B192C] mt-1">
              {t("students")}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={exportCsv} data-testid="export-students-btn">
              <Download size={16} className="me-2" />
              {lang === "ar" ? "تصدير Excel" : "Export to Excel"}
            </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#7A1B28] hover:bg-[#601520] text-white" data-testid="add-student-btn">
                <Plus className="me-2 h-4 w-4" /> {t("addStudent")}
              </Button>
            </DialogTrigger>
            <NewStudentDialog meta={meta} onClose={() => { setOpen(false); load(); }} />
          </Dialog>
          </div>
        </div>

        <Card className="p-4 border border-slate-200 shadow-none mb-6">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={16} className="absolute inset-y-0 my-auto ms-3 text-slate-400" />
              <Input placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)}
                     className="ps-9" data-testid="students-search" />
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-56" data-testid="stage-filter">
                <SelectValue placeholder={t("stage")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                {meta.stages.map((s) => <SelectItem key={s} value={s}>{ts(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {isAdmin && selected.length > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50"
               data-testid="bulk-actions-bar">
            <span className="text-sm text-red-900">
              {lang === "ar"
                ? `تم تحديد ${selected.length} طالب`
                : `${selected.length} student(s) selected`}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setSelected([])}
                      className="text-slate-600 hover:bg-white">
                {lang === "ar" ? "إلغاء التحديد" : "Clear"}
              </Button>
              <Button onClick={deleteSelected} disabled={deleting}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="delete-selected-btn">
                <Trash2 size={16} className="me-2" />
                {deleting
                  ? (lang === "ar" ? "جارِ الحذف…" : "Deleting…")
                  : (lang === "ar" ? "حذف المحدد" : "Delete selected")}
              </Button>
            </div>
          </div>
        )}

        <Card className="border border-slate-200 shadow-none overflow-x-auto" data-testid="students-list">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                {isAdmin && (
                  <th className="px-4 py-3 w-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                              aria-label={lang === "ar" ? "تحديد الكل" : "Select all"}
                              data-testid="select-all-students" />
                  </th>
                )}
                <th className="px-4 py-3">{t("name")}</th>
                <th className="px-4 py-3">{t("nationality")}</th>
                <th className="px-4 py-3">{t("educationLevel")}</th>
                <th className="px-4 py-3">{t("targetCountries")}</th>
                <th className="px-4 py-3">{t("stage")}</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${selected.includes(s.id) ? "bg-red-50/40" : ""}`}>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <Checkbox checked={selected.includes(s.id)}
                                onCheckedChange={() => toggleOne(s.id)}
                                aria-label={s.name}
                                data-testid={`select-student-${s.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Link to={`/students/${s.id}`} className="font-medium text-[#0B192C] hover:text-[#7A1B28]"
                          data-testid={`student-row-${s.id}`}>
                      {s.name}
                    </Link>
                    <div className="text-xs text-slate-500">{s.email || s.phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{s.nationality || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{s.education_level || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <div className="flex flex-wrap gap-1">
                      {(s.target_countries || []).map((c) => (
                        <span key={c} className="px-2 py-0.5 text-xs rounded-md bg-slate-100">{tc(c)}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-1 rounded-md border ${STAGE_STYLES[s.stage] || ""}`}>
                      {ts(s.stage)}
                    </span>
                  </td>
                </tr>
              ))}
              {students.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 5} className="px-4 py-10 text-center text-slate-500">{t("noStudents")}</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </Layout>
  );
}

function NewStudentDialog({ meta, onClose }) {
  const { t, ts, tc } = useI18n();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", nationality: "", education_level: "",
    passport_number: "",
    target_countries: [], stage: "New Lead", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const toggleCountry = (c) => {
    setForm((f) => ({
      ...f,
      target_countries: f.target_countries.includes(c)
        ? f.target_countries.filter((x) => x !== c)
        : [...f.target_countries, c],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/students", form);
      toast.success(t("addStudent"));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="new-student-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-[#0B192C]">{t("newStudent")}</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>{t("name")}</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="new-student-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("phone")}</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   data-testid="new-student-phone" />
          </div>
          <div>
            <Label>{t("email")}</Label>
            <Input type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="new-student-email" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("nationality")}</Label>
            <Input value={form.nationality}
                   onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                   data-testid="new-student-nationality" />
          </div>
          <div>
            <Label>{t("educationLevel")}</Label>
            <Input value={form.education_level}
                   onChange={(e) => setForm({ ...form, education_level: e.target.value })}
                   placeholder="High School / Bachelor / Master…"
                   data-testid="new-student-education" />
          </div>
        </div>
        <div>
          <Label>{t("passportNumber")}</Label>
          <Input value={form.passport_number}
                 onChange={(e) => setForm({ ...form, passport_number: e.target.value })}
                 data-testid="new-student-passport" />
        </div>
        <div>
          <Label>{t("targetCountries")}</Label>
          <div className="grid grid-cols-3 gap-2 mt-2 max-h-40 overflow-y-auto pe-1">
            {meta.countries.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.target_countries.includes(c)}
                          onCheckedChange={() => toggleCountry(c)}
                          data-testid={`country-${c}`} />
                <span>{tc(c)}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label>{t("stage")}</Label>
          <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
            <SelectTrigger data-testid="new-student-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meta.stages.map((s) => <SelectItem key={s} value={s}>{ts(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving}
                  className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                  data-testid="new-student-save">
            {t("save")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
